
import { defineComponent } from "vue";
// Parts
import InputField from "@/views/shared/form-parts/InputField.vue";
import CommonInput from "@/views/shared/form-parts/CommonInput.vue";

// Sections
import TravelTerm from "@/views/order-form/select-plan/component/TravelTerm.vue";
import ReceivingMethod from "@/views/order-form/select-plan/component/ReceivingMethod.vue";
import AreaSelect from "@/views/order-form/select-plan/component/AreaSelect.vue";
import WiFiProductSelect from "@/views/order-form/select-plan/component/WiFiProductSelect.vue";
import OptionalInsurance from "@/views/order-form/select-plan/component/OptionalInsurance.vue";
import OptionalProducts from "@/views/order-form/select-plan/component/OptionalProducts.vue";
import ReturningMethod, {
  RETURNING_METHOD_OPTIONS as returningMethodOptions,
} from "@/views/order-form/select-plan/component/ReturningMethod.vue";
import CouponCodes from "@/views/order-form/select-plan/component/CouponCodes.vue";
import EstimationResult from "@/views/order-form/select-plan/component/EstimationResult.vue";
import PaymentMethod from "@/views/order-form/select-plan/component/PaymentMethod.vue";

// Interfaces
import { NestedSelectOptions } from "@/views/shared/form-parts/PullDownSelect.vue";
import {
  AreaList,
  CountryList,
  EstimationData,
  Product,
} from "@/views/order-form/order-form-interfaces";
import {
  SelectPlanData,
  SelectPlanFormData,
  SelectPlanFormStatus,
} from "@/views/order-form/select-plan/select-plan-interfaces";

// Tools
import Utility from "@/assets/common/scripts/utility";
import { string } from "yup";
import { useGlobalStore } from "@/stores/global";

// Validation
import { Form } from "vee-validate";
import { validationMessages as message } from "@/assets/validation/validation-messages";

// API
import OrderService, {
  prepareCountryParams,
  prepareEstimationParams,
} from "@/api/order.service";
import { UserLoginParams } from "@/api/login.service";
import { AxiosError } from "axios";
import loginUtil from "@/assets/common/scripts/loginUtil";

// Constants
export const PRODUCT_CATEGORIES = {
  daily: "1",
  pack: "0",
  monthlyPack: "2",
  option: "3",
};
export const ESTIMATION_DATA_DEFAULT: EstimationData = {
  orderInfo: {
    order_grand_total: 0,
    zaiko_flag: "0",
  },
  detailInfo: [
    {
      pro_name_show: "",
      pro_qty_show: "",
      pro_amount: "",
      pro_amount_tax: "",
    },
  ],
};
export const SELECT_PLAN_FORM_DATA_DEFAULT: SelectPlanFormData = {
  order_from_date: "",
  order_to_date: "",
  order_get_type: "1",
  order_send_pref_id: "",
  countries: [], // must be a parallel when it's posted
  order_pro_name_id: "", // must be a Number type when it's posted
  order_pro_qty: "1", // must be a Number type when it's posted
  order_pro_insurance: "1",
  order_option: [],
  order_return_type: returningMethodOptions.delivery,
  order_camp_code: "",
  order_shop_code: "",
  order_pay_method: "3",
  shop_type: "",
};
export const SELECT_PLAN_FORM_STATUS_DEFAULT: SelectPlanFormStatus = {
  isSearchedWiFi: false,
  isEstimated: false,
  allProducts: [],
  estimationData: { ...ESTIMATION_DATA_DEFAULT },
  estimationErrorMessage: "",
  loginErrorMessage: "",
};

export default defineComponent({
  name: "selectPlan",
  components: {
    InputField,
    CommonInput,
    Form,
    TravelTerm,
    ReceivingMethod,
    AreaSelect,
    WiFiProductSelect,
    OptionalInsurance,
    OptionalProducts,
    ReturningMethod,
    CouponCodes,
    EstimationResult,
    PaymentMethod,
  },
  props: {
    areaList: {
      type: Array as () => AreaList[],
      default: () => [],
    },
    prefectureList: {
      type: Array as () => NestedSelectOptions[],
      default: () => [],
    },
    countryList: {
      type: Array as () => CountryList[],
      default: () => [],
    },
  },
  watch: {
    // Reset Wi-Fi Plan recommendation when the condition has changed
    "formData.order_from_date": {
      handler: "resetWiFiProducts",
    },
    "formData.order_to_date": {
      handler: "resetWiFiProducts",
    },
    // "formData.order_get_type": {
    //   handler: "resetWiFiProducts",
    // },
    "formData.countries": {
      handler: "resetWiFiProducts",
    },
    formData: {
      handler: "resetEstimation",
      deep: true,
    },
  },
  computed: {
    globalState() {
      return useGlobalStore();
    },
    productCategories() {
      return PRODUCT_CATEGORIES;
    },
    wifiProducts(): Product[] {
      return this.formStatus.allProducts.filter((product) => {
        return (
          product.pro_typeflg === this.productCategories.daily ||
          product.pro_typeflg === this.productCategories.pack ||
          product.pro_typeflg === this.productCategories.monthlyPack
        );
      });
    },
    optionalProducts(): Product[] {
      return this.formStatus.allProducts.filter((product) => {
        return product.pro_typeflg === this.productCategories.option;
      });
    },
    mailAddressValidRules(): object {
      if (!this.globalState.$state.loginStatus.isLoggedIn)
        return string()
          .required(message.required)
          .email(message.email)
          .max(100, message.max);
      return {};
    },
    isInStock(): boolean {
      return !!Number(this.formStatus.estimationData.orderInfo.zaiko_flag);
    },
  },
  data(): SelectPlanData {
    return {
      formData: { ...SELECT_PLAN_FORM_DATA_DEFAULT },
      formStatus: { ...SELECT_PLAN_FORM_STATUS_DEFAULT },
      userInfo: {
        mail_address: "",
        passWord: "",
      },
    };
  },
  methods: {
    onInvalidSubmit(): void {
      Utility.scrollToInvalidElement();
    },
    resetWiFiProducts(): void {
      this.formStatus.allProducts = [];
      this.formStatus.isSearchedWiFi = false;
    },
    resetEstimation(): void {
      this.formStatus.estimationData = ESTIMATION_DATA_DEFAULT;
      this.formStatus.estimationErrorMessage = "";
      this.formStatus.isEstimated = false;
    },
    onSearchProducts(): void {
      useGlobalStore().showLoading();
      OrderService.getProductLists(
        prepareCountryParams(this.formData.countries)
      ).then(({ data: res }) => {
        if ((res.result !== "0000" && res.errormsg) || !res.data) {
          throw new AxiosError(res.errormsg);
        }
        this.formStatus.allProducts = res.data;
        useGlobalStore().hideLoading();
      });
      this.formStatus.isSearchedWiFi = true;
    },
    onUpdateCountries(countries: string[]): void {
      this.formData.countries = countries;
    },
    onWiFiUpdated(value: string): void {
      this.formData.order_pro_name_id = value;
    },
    onOptionUnitUpdated(selectedOptions: []): void {
      this.formData.order_option = selectedOptions;
    },
    onShowEstimation(): void {
      useGlobalStore().showLoading();
      const params = prepareEstimationParams(this.formData);
      OrderService.getEstimation(params)
        .then(({ data: res }) => {
          if (res.result !== "0000" && res.errormsg) {
            throw new AxiosError(res.errormsg);
          }
          if (!res.data) return;
          this.formStatus.estimationData = res.data;
          this.formStatus.isEstimated = true;
        })
        .catch((error) => {
          this.formStatus.estimationErrorMessage = error.message;
          this.formStatus.isEstimated = false;
        })
        .finally(() => {
          useGlobalStore().hideLoading();
        });
    },
    onForwardToCustomerProfile(): void {
      const value = { formStatus: this.formStatus, formData: this.formData };
      this.$emit("forwardToCustomerProfile", value);
    },
    onForwardWithLogin(): void {
      useGlobalStore().showLoading();
      const params: UserLoginParams = {
        member_email: this.userInfo.mail_address,
        member_passwd: this.userInfo.passWord,
      };
      loginUtil
        .login(params)
        .then(() => {
          this.onForwardToCustomerProfile();
          this.formStatus.loginErrorMessage = "";
        })
        .catch((error) => {
          this.formStatus.loginErrorMessage = error.message;
        })
        .finally(() => {
          useGlobalStore().hideLoading();
        });
    },
  },
  emits: ["forwardToCustomerProfile"],
});
